'use client'

import { ComponentProps, forwardRef } from 'react'
import styled from 'styled-components'

import { DropdownButton, DropdownButtonProps, Menu, TypeStyle } from '@syconium/little-miss-figgy'

const FilterMenuItemButton = styled.button<{ $active?: boolean }>`
  ${TypeStyle.css.bodyMicro}
  display: block;
  width: 100%;
  text-align: left;
  padding: ${o => o.theme.spacing(1, 2)};
  font-weight: ${o => (o.$active ? 700 : 500)};
  &[aria-disabled='true'] {
    color: ${o => o.theme.color.stroke.disabled.text.on.background};
    cursor: not-allowed;
  }
  &[data-active-item] {
    &:not([aria-disabled='true']) {
      background-color: ${o => o.theme.color.fill.subtle};
    }
  }
`

/** The base dropdown button that is rendered as the underlying element for `FilterMenuButton` and `FilterMenubarButton`. */
const StatefulDropdownButton = forwardRef<HTMLButtonElement, DropdownButtonProps<'button'>>(
  function StatefulDropdownButton({ children, ...rest }, ref) {
    const menu = Menu.useState()
    const active = menu?.open ?? false

    return (
      <DropdownButton ref={ref} onClick={() => alert('huh')} as='button' active={active} {...rest}>
        {children}
      </DropdownButton>
    )
  }
)

/** The button trigger responsible for opening the filter menu. */
export const FilterMenuButton = forwardRef<HTMLButtonElement, DropdownButtonProps<'button'>>(
  function FilterMenuButton({ children, ...rest }, ref) {
    return (
      <Menu.MenuButton ref={ref} render={<StatefulDropdownButton {...rest} />}>
        {children}
      </Menu.MenuButton>
    )
  }
)

export const FilterMenubarButton = forwardRef<HTMLDivElement, DropdownButtonProps<'button'>>(
  function FilterMenubarButton({ children, ...rest }, ref) {
    return (
      <Menu.MenubarButton ref={ref} render={<StatefulDropdownButton {...rest} />}>
        {children}
      </Menu.MenubarButton>
    )
  }
)

/** An individual interactive menu item displayed within the filter menu. */
export const FilterMenuItem = forwardRef<
  HTMLDivElement,
  { active?: boolean } & ComponentProps<typeof Menu.MenuItem>
>(function FilterMenuItem({ children, active, ...rest }, ref) {
  return (
    <Menu.MenuItem
      ref={ref}
      aria-current={active}
      render={<FilterMenuItemButton $active={active} />}
      accessibleWhenDisabled
      {...rest}
    >
      {children}
    </Menu.MenuItem>
  )
})

export default {
  /** The top-level provider for the menu. */
  Provider: Menu.Provider,
  /** The enclosing container for the individual menu items for the filter menu. */
  Menu: Menu.Menu,
  MenuButton: FilterMenuButton,
  MenubarButton: FilterMenubarButton,
  Item: FilterMenuItem,
}
